import { FC, useState, useEffect } from 'react';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import CloseIcon from '@material-ui/icons/Close';
import { TextField, Button, Grid, Modal, Paper, IconButton } from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import { CustomerFormProps } from './types';
const fieldTypeOptions = [
  { label: 'Text', value: 'text' },
  { label: 'Date', value: 'date' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Select', value: 'select' },
];
export const EndUserCustomForm: FC<CustomerFormProps> = observer(({ open, handleClose, selectedRow, refetch }) => {
  const classes = useStyles();
  const {
    endUserSettingsFormStore: { AttributeGenrate, UpdateAttribute },
    notificationStore: { openNotification },
  } = useStore();
  const [loading, setLoading] = useState(false);
  const AttributeGenratereq = useMutation((data: any) => AttributeGenrate(data), {
    mutationKey: 'AttributeGenratereq',
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      openNotification('success', 'Saved successfully!');
      reset({
        label: '',
        options: '',
      });
      setValue('type', null);
      setKey((prevKey) => prevKey + 1);
      refetch();
      handleClose();
      setLoading(false);
    },
    onError: (e) => {
      openNotification('error', `Field is required`);
      setLoading(false);
    },
  });

  const UpdateAttributereq = useMutation((data: any) => UpdateAttribute(data), {
    mutationKey: 'UpdateAttributereq',
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      refetch();
      handleClose();
      openNotification('success', 'Updated successfully!');
      setLoading(false);
    },
    onError: (e) => {
      openNotification('error', `Field is required`);
      setLoading(false);
    },
  });

  useAppPreloader(AttributeGenratereq.isLoading || UpdateAttributereq.isLoading || loading);
  const { control, handleSubmit, watch, reset, setValue } = useForm<any>();
  const selectedType = watch('type');
  useEffect(() => {
    if (selectedRow) {
      reset({ ...selectedRow });
      setValue('type', selectedRow.type);
    } else {
      reset({ label: '', options: '', type: '' });
    }
  }, [selectedRow, reset, setValue]);

  const [key, setKey] = useState(0);

  const onSubmit = async (data: any) => {
    const formattedData = selectedRow
      ? {
          label: selectedRow.label,
          newLabel: data.label,
          type: data.type,
          name: selectedRow.name,
          value: selectedRow.value || null,
          options:
            (data.type === 'select' || data.type === 'checkbox') && data.options
              ? Array.isArray(data.options)
                ? data.options
                : data.options.split(',').map((opt: string) => opt.trim())
              : [],
        }
      : {
          customAttribute: [
            {
              type: data.type,
              label: data.label,
              value: null,
              name: data.label,
              options:
                (data.type === 'select' || data.type === 'checkbox') && data.options
                  ? Array.isArray(data.options)
                    ? data.options
                    : data.options.split(',').map((opt: string) => opt.trim())
                  : [],
            },
          ],
        };
    if (selectedRow) {
      UpdateAttributereq.mutate(formattedData, {
        onSuccess: () => {
          openNotification('success', 'Updated successfully!');
          setLoading(true);
          refetch();
          handleClose();
        },
      });
    } else {
      AttributeGenratereq.mutate(formattedData, {
        onSuccess: () => {
          openNotification('success', 'Saved successfully!');
          setLoading(true);
          reset({
            label: '',
            options: '',
          });
          setValue('type', null);
          setKey((prevKey) => prevKey + 1);
        },
      });
    }
  };

  useEffect(() => {
    if (selectedRow?.options) {
      setValue('options', selectedRow.options);
    }
  }, [selectedRow, setValue]);
  return (
    <>
      <Modal disablePortal disableEnforceFocus disableAutoFocus open={open} className={classes.modalContainer}>
        <Grid container justify='center' alignItems='center' style={{ minHeight: '100vh' }}>
          <Paper className={classes.paperModel}>
            <Grid container justify='space-between' alignItems='center'>
              {selectedRow ? <p>Edit Custom Attribute</p> : <p> Add Custom Attribute</p>}
              <IconButton
                style={{ marginRight: -16 }}
                onClick={() => {
                  reset({ label: '', options: '', type: '' });
                  handleClose();
                }}
              >
                <CloseIcon color='primary' />
              </IconButton>
            </Grid>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                key={key}
                name='type'
                control={control}
                defaultValue={selectedRow?.type || ''}
                rules={{ required: 'Field Type is required' }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    options={fieldTypeOptions}
                    getOptionLabel={(option) => option.label}
                    value={fieldTypeOptions.find((opt) => opt.value === field.value) || null}
                    onChange={(_, value) => field.onChange(value?.value || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        label='Field Type'
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name='label'
                control={control}
                defaultValue={selectedRow?.label || ''}
                rules={{ required: 'Label is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant='outlined'
                    label='Label'
                    fullWidth
                    style={{ margin: '16px 0px' }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              {(selectedType === 'select' || selectedType === 'checkbox') && (
                <Controller
                  name='options'
                  control={control}
                  defaultValue={selectedRow?.options ? selectedRow.options.join(', ') : ''}
                  rules={{ required: 'Options are required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      variant='outlined'
                      label='Options (comma separated)'
                      fullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : 'Enter options separated by commas'}
                    />
                  )}
                />
              )}
              <div className={classes.leftCol}>
                <Grid item xs={12} className={classes.btnContainer}>
                  {selectedRow ? (
                    <Button className={classes.submitBtn} type='submit' variant='contained'>
                      Update
                    </Button>
                  ) : (
                    <Button className={classes.submitBtn} type='submit' variant='contained'>
                      Save
                    </Button>
                  )}
                </Grid>
              </div>
            </form>
          </Paper>
        </Grid>
      </Modal>
    </>
  );
});
